
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import FullScreenDialog from "../../full-screen-dialog/FullScreenDialog";
import SettingsBar, { SettingsActionEnum } from "../../settings-bar/SettingsBar";
import { ContentContainer } from "../../content-container/ContentContainer";
import { ContentSection } from "../../content-section/ContentSection";
import LoadingZone from "../../loading-zone/LoadingZone";
import { IQuickTrendViewModel } from "../../../services/trending-data-service/Models";
import { useCallback, useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { useGraphReportOnScreenData } from "../../../queries/graph-report/UseGraphReportOnscreenData";
import NoData from "../../no-data/NoData";
import GraphReport from "../GraphReport";

export type GraphReportViewDialogValues = {
    fileStoreId: number | undefined;
    messageDate: Date | undefined;
}

export type GraphReportViewDialogResult = DialogResult<GraphReportViewDialogValues>;

export type GraphReportViewDialogProps = {
} & DialogProps<GraphReportViewDialogValues, GraphReportViewDialogResult>;

export function GraphReportViewDialog({
    open,
    onClose,
    values
}: GraphReportViewDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const { t } = useTranslation();

    const [reportData, setReportData] = useState<IQuickTrendViewModel | undefined>(undefined);

    const {
        data: fetchedData,
        isFetching: isFetchingOnscreenData,
    } = useQueryNotifications(useGraphReportOnScreenData)({
        groupID: routeContext.groupID as number,
        fileStoreID: values?.fileStoreId as number,
        enabled: (values?.fileStoreId ?? 0) > 0,
    });

    // #region Callback Functions

    const getNoDataMessage = useCallback((): string | JSX.Element | undefined => {

        let noDataTitle: string = t("noData.noDataFound");

        return reportData?.quickTrendObjects && ((reportData?.quickTrendObjects?.length ?? 0) === 0)
                ? noDataTitle
                : undefined;
    },
        [
            reportData,
            t
        ]);

    const getNoDataTitle = useCallback((): string | undefined => {

        return reportData?.quickTrendObjects && ((reportData?.quickTrendObjects?.length ?? 0) === 0)
                ? t("noData.title") as string
                : undefined;
    },
        [
            reportData,
            t
        ]);
    // #endregion Callback Functions

    // #region Effects

    useEffect(() => {

        setReportData(fetchedData);

    }, [
        fetchedData,
        setReportData
    ]);

    // #endregion Effects

    const onCloseDialog = (reason: string) => {

        reason === "ok"
            ? onCloseGraphView(SettingsActionEnum.OK)
            : onCloseGraphView(SettingsActionEnum.Close);
    }

    const onCloseGraphView = (action: SettingsActionEnum): void => {

        if (action === SettingsActionEnum.OK) {

            onOk();
        } else {
            onCancel();
        }
    }

    const onCancel = () => {

        onClose && onClose({
            reason: "cancel",
            values: undefined
        });
    };

    const onOk = () => {

        onClose && onClose({
            reason: "ok"
        });
    };

    const noDataTitle = getNoDataTitle();
    const noDataMessage = getNoDataMessage();

    return (
        <FullScreenDialog open={open} onClose={onCloseDialog}>

            <SettingsBar title={''}
                hideAction={true}
                onClose={onCloseGraphView}
            />

            <LoadingZone isLoading={isFetchingOnscreenData}>
                <ContentContainer>
                    <ContentSection>
                        {
                            noDataMessage &&
                            <NoData
                                title={noDataTitle}
                                text={noDataMessage}
                            />
                        }

                        {!noDataMessage && !isFetchingOnscreenData &&
                            ((reportData?.quickTrendObjects?.length ?? 0) > 0) &&
                            <GraphReport
                                data={reportData}
                            />
                        }

                    </ContentSection>
                </ContentContainer>
            </LoadingZone>
        </FullScreenDialog>
    );
}

export type GraphReportViewDialogState = DialogState<GraphReportViewDialogValues, GraphReportViewDialogResult>;
export type BindGraphReportViewDialogReturn = BindDialogReturn<GraphReportViewDialogValues, GraphReportViewDialogResult>;

export function useGraphReportViewDialogState(): GraphReportViewDialogState {
    return useDialogState();
}

export function bindGraphReportViewDialog(props: GraphReportViewDialogState): BindGraphReportViewDialogReturn {
    return bindDialog(props);
}
